import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import { useFormik } from "formik";
import {
  BackArrow,
  Button,
  CheckBox,
  ClipBoardWrapper,
  Input,
  Select,
  TextEditor,
  TopProgress,
  Typography,
} from "../../../../components";
import { useUi } from "../../../../context/ui.context";
import { messageTemplatesService } from "../../../../api";
import { usePageTitle } from "../../../../context/navigationContext";
import {
  Container,
  Content,
  FormSection,
  Header,
  SidebarSection,
  AddLanguageControls,
  AddLanguageSection,
  HelpSection,
  TemplateBlock,
  TemplateHeader,
  VariableTag,
  VariablesHint,
  VariablesWrapper,
  FormActions,
} from "./styles";
import { extractErrorMessage } from "../../../../utils/error-extractor";

type MessageTemplate = {
  id: number | string;
  name: string;
  title: string;
  content: string;
  category: string;
  category_display: string;
  language: string;
  language_display: string;
  target: string;
  target_category: string | null;
  type: string;
  disable_notification: boolean;
};

type CreateOptionsCategory = {
  id: string;
  name: string;
  description: string;
};

type MessageTemplateCreateOptions = {
  languages: Array<{
    language_display: string;
    language_code: string;
  }>;
  help_text_map?: Record<
    string,
    {
      variables?: string[];
      detail?: string;
    }
  >;
  target: Record<string, string>;
  target_category: Record<string, string>;
  categories: CreateOptionsCategory[];
  category_variables: Record<string, string[]>;
};

const EMAIL_ONLY_TEMPLATES = [
  "account.user.invitation",
  "account.reset.password",
  "account.signup.confirm",
  "openstack.error",
];

const MessageTemplateEdit: FC = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId: string }>();
  const { viewAlert } = useUi();
  usePageTitle(t("EDIT_MESSAGE_TEMPLATE"));

  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [createOptions, setCreateOptions] = useState<MessageTemplateCreateOptions | null>(null);
  const [editableTemplates, setEditableTemplates] = useState<MessageTemplate[]>([]);
  const [currentTemplate, setCurrentTemplate] = useState<MessageTemplate | null>(null);
  const [editLoading, setEditLoading] = useState<boolean>(false);
  const [addLanguageLoading, setAddLanguageLoading] = useState<boolean>(false);
  const [deleteLoadingId, setDeleteLoadingId] = useState<number | string | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string>("");

  const loadTemplates = useCallback(() => {
    setPageLoading(true);
    Promise.all([
      messageTemplatesService.getCreateOptions(),
      messageTemplatesService.getEditableTemplates(templateId!),
    ])
      .then(([optionsRes, templatesRes]) => {
        setCreateOptions(optionsRes.data as MessageTemplateCreateOptions);
        // API returns {objects: [...]} not just array
        const responseData = templatesRes.data;
        const data = (Array.isArray(responseData)
          ? responseData
          : responseData?.objects || []) as MessageTemplate[];

        if (!data.length) {
          viewAlert({
            severity: "warning",
            message: t("NO_TEMPLATE_VARIANTS_FOUND"),
          });
          navigate("/settings/message-templates");
          return;
        }
        setEditableTemplates(data);
        setCurrentTemplate(data[0]);
      })
      .catch((error) => {
        const message =
          error?.response?.data?.detail ||
          extractErrorMessage(error?.response?.data) ||
          t("FAILED_TO_LOAD_TEMPLATE");
        viewAlert({
          severity: "error",
          message,
        });
        navigate("/settings/message-templates");
      })
      .finally(() => {
        setPageLoading(false);
      });
  }, [templateId, t, viewAlert, navigate]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const targetOptions = useMemo(() => {
    if (!createOptions) {
      return [];
    }
    return Object.entries(createOptions.target || {}).map(([value, label]) => ({
      id: value,
      value,
      label,
    }));
  }, [createOptions]);

  const targetCategoryOptions = useMemo(() => {
    if (!createOptions) {
      return [];
    }
    const entries = Object.entries(createOptions.target_category || {});
    return [
      { id: "", value: "", label: t("NONE") },
      ...entries.map(([value, label]) => ({ id: value, value, label })),
    ];
  }, [createOptions, t]);

  const editCategory = useMemo(() => {
    if (!createOptions || !currentTemplate) {
      return null;
    }
    return (createOptions.categories || []).find(
      (category) => category.id === currentTemplate.category
    ) || null;
  }, [createOptions, currentTemplate]);

  const variables = useMemo(() => {
    if (!createOptions || !editCategory || currentTemplate?.type !== "custom") {
      return [];
    }
    const variablesMap = createOptions.category_variables || {};
    return variablesMap[editCategory.name] || [];
  }, [createOptions, editCategory, currentTemplate]);

  const helpText = useMemo(() => {
    if (!createOptions || !currentTemplate || currentTemplate.type !== "system") {
      return null;
    }
    return createOptions.help_text_map?.[currentTemplate.name] || null;
  }, [createOptions, currentTemplate]);

  const isEmailOnly = useMemo(() => {
    if (!currentTemplate) {
      return false;
    }
    return EMAIL_ONLY_TEMPLATES.includes(currentTemplate.name);
  }, [currentTemplate]);

  const isTargetCategoryDisabled = useMemo(() => {
    if (!currentTemplate) {
      return false;
    }
    return currentTemplate.type === "system";
  }, [currentTemplate]);

  const availableLanguages = useMemo(() => {
    if (!createOptions) {
      return [];
    }
    const existingLanguages = new Set(
      editableTemplates.map((template) => template.language)
    );
    return (createOptions.languages || []).filter(
      (language) => !existingLanguages.has(language.language_code)
    );
  }, [createOptions, editableTemplates]);

  const availableLanguageOptions = useMemo(
    () =>
      availableLanguages.map((language) => ({
        id: language.language_code,
        value: language.language_code,
        label: language.language_display,
      })),
    [availableLanguages]
  );

  useEffect(() => {
    if (!availableLanguages.length) {
      setSelectedLanguage("");
      return;
    }

    const found = availableLanguages.find(
      (language) => language.language_code === selectedLanguage
    );
    if (!found) {
      setSelectedLanguage(availableLanguages[0].language_code);
    }
  }, [availableLanguages, selectedLanguage]);

  const validationSchema = useMemo(() => {
    const shape: Record<string, any> = {
      disable_notification: yup.boolean(),
      target: yup.string().required(t("REQUIRED_FIELD")),
      target_category: yup.string().nullable(),
    };
    editableTemplates.forEach((template) => {
      shape[`title_${template.id}`] = yup.string().required(t("REQUIRED_FIELD"));
      shape[`content_${template.id}`] = yup.string().required(t("REQUIRED_FIELD"));
    });
    return yup.object().shape(shape);
  }, [editableTemplates, t]);

  const initialValues = useMemo(() => {
    const values: Record<string, any> = {
      disable_notification: editableTemplates[0]?.disable_notification || false,
      target: editableTemplates[0]?.target || "",
      target_category: editableTemplates[0]?.target_category || "",
    };
    editableTemplates.forEach((template) => {
      values[`title_${template.id}`] = template.title || "";
      values[`content_${template.id}`] = template.content || "";
    });
    return values;
  }, [editableTemplates]);

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema,
    initialValues,
    onSubmit: (formValues) => {
      if (!editableTemplates.length) {
        return;
      }
      setEditLoading(true);
      setPageLoading(true);
      const payload = editableTemplates.map((template) => ({
        ...template,
        title: formValues[`title_${template.id}`],
        content: formValues[`content_${template.id}`],
        disable_notification: formValues.disable_notification,
        target: formValues.target,
        target_category: formValues.target_category || "",
      }));
      messageTemplatesService
        .updateTemplatesSet(payload)
        .then(() => {
          viewAlert({
            severity: "information",
            message: t("TEMPLATES_UPDATED"),
          });
          loadTemplates();
        })
        .catch((error) => {
          const backendErrors = error?.response?.data;
          if (backendErrors?.detail) {
            viewAlert({
              severity: "error",
              message: backendErrors.detail,
            });
          } else if (backendErrors) {
            setErrors(backendErrors);
          } else {
            viewAlert({
              severity: "error",
              message: t("FAILED_TO_SAVE_TEMPLATE"),
            });
          }
        })
        .finally(() => {
          setEditLoading(false);
          setPageLoading(false);
        });
    },
  });

  const handleAddLanguage = useCallback(() => {
    if (!currentTemplate?.name || !selectedLanguage) {
      return;
    }

    setAddLanguageLoading(true);
    messageTemplatesService
      .createLanguageTemplate(currentTemplate.name, selectedLanguage)
      .then(() => {
        viewAlert({
          severity: "information",
          message: t("TEMPLATE_LANGUAGE_CREATED"),
        });
        loadTemplates();
      })
      .catch((error) => {
        const message =
          error?.response?.data?.detail ||
          extractErrorMessage(error?.response?.data) ||
          t("FAILED_TO_ADD_TEMPLATE_LANGUAGE");
        viewAlert({
          severity: "error",
          message,
        });
      })
      .finally(() => {
        setAddLanguageLoading(false);
      });
  }, [currentTemplate, selectedLanguage, t, viewAlert, loadTemplates]);

  const handleDeleteLanguage = useCallback(
    (template: MessageTemplate) => {
      const confirmed = window.confirm(
        t("CONFIRM_DELETE_TEMPLATE", {
          name: currentTemplate?.name || template.name,
          language: template.language_display,
        })
      );
      if (!confirmed) {
        return;
      }

      setDeleteLoadingId(template.id);
      messageTemplatesService
        .delete(template.id)
        .then(() => {
          viewAlert({
            severity: "information",
            message: t("TEMPLATE_DELETED"),
          });
          loadTemplates();
        })
        .catch((error) => {
          const message =
            error?.response?.data?.detail ||
            extractErrorMessage(error?.response?.data) ||
            t("FAILED_TO_DELETE_TEMPLATE");
          viewAlert({
            severity: "error",
            message,
          });
        })
        .finally(() => {
          setDeleteLoadingId(null);
        });
    },
    [currentTemplate, t, viewAlert, loadTemplates]
  );

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setErrors,
  } = formik;

  const onFormSubmit = useCallback((e?: React.FormEvent) => {
    e?.preventDefault();
    formik.submitForm();
  }, [formik]);


  if (pageLoading || !currentTemplate || !createOptions) {
    return (
      <Container>
        <TopProgress loading={true} />
        <Typography variant="body">{t("LOADING_TEMPLATE")}</Typography>
      </Container>
    );
  }

  const formikProps = {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
  };

  return (
    <Container>
      <TopProgress loading={pageLoading} />
      <Header>
        <BackArrow fallbackPath="/settings/message-templates" />
        <Typography variant="h1">
          {t("EDIT_TEMPLATE_TITLE", { name: currentTemplate.name })}
        </Typography>
        <Typography variant="body">
          {t("CURRENT_CATEGORY", { category: currentTemplate.category_display })}
        </Typography>
      </Header>
      <Content>
        <FormSection>
          <CheckBox
            title={t("DISABLE_NOTIFICATION")}
            value={!!values.disable_notification}
            onChange={() =>
              setFieldValue("disable_notification", !values.disable_notification)
            }
          />
          <Select
            required
            title={t("TARGET")}
            data={targetOptions}
            selectedValue={values.target}
            onChange={(value: string) => setFieldValue("target", value)}
            placeholder={t("CHOOSE")}
            fieldName="target"
            disabled={isEmailOnly}
            {...formikProps}
          />
          <Select
            title={t("TARGET_CATEGORY")}
            data={targetCategoryOptions}
            selectedValue={values.target_category ?? ""}
            onChange={(value: string) => setFieldValue("target_category", value)}
            placeholder={t("CHOOSE")}
            fieldName="target_category"
            disabled={isTargetCategoryDisabled}
            {...formikProps}
          />
          {availableLanguageOptions.length > 0 ? (
            <AddLanguageSection>
              <Typography variant="h3">
                {t("ADD_TEMPLATE_LANGUAGE")}
              </Typography>
              <AddLanguageControls>
                <div style={{ minWidth: "240px", flex: "1 1 240px" }}>
                  <Select
                    title={t("LANGUAGE")}
                    data={availableLanguageOptions}
                    selectedValue={selectedLanguage}
                    onChange={(value: string) => setSelectedLanguage(value)}
                  />
                </div>
                <Button
                  variant="stroke"
                  size="display"
                  title={t("ADD")}
                  loading={addLanguageLoading}
                  onClick={handleAddLanguage}
                />
              </AddLanguageControls>
            </AddLanguageSection>
          ) : null}
          {editableTemplates.map((template) => {
            const titleField = `title_${template.id}`;
            const contentField = `content_${template.id}`;
            return (
              <TemplateBlock key={template.id}>
                <TemplateHeader>
                  <Typography variant="h3">
                    {t("LANGUAGE_TEMPLATE_BLOCK", { language: template.language_display })}
                  </Typography>
                  {template.id !== editableTemplates[0]?.id ? (
                    <Button
                      variant="stroke"
                      size="small"
                      title={t("DELETE")}
                      loading={deleteLoadingId === template.id}
                      onClick={() => handleDeleteLanguage(template)}
                    />
                  ) : null}
                </TemplateHeader>
                <Input
                  required
                  title={t("TITLE")}
                  placeholder=""
                  fieldName={titleField}
                  {...formikProps}
                />
                <TextEditor
                  onChange={(value: string) => setFieldValue(contentField, value)}
                  fieldName={contentField}
                  values={values}
                  errors={errors}
                />
              </TemplateBlock>
            );
          })}
          <FormActions>
            <Button
              variant="stroke"
              size="display"
              title={t("CANCEL")}
              onClick={() => navigate("/settings/message-templates")}
            />
            <Button
              variant="primary"
              size="display"
              title={t("SAVE")}
              loading={editLoading}
              onClick={onFormSubmit}
            />
          </FormActions>
        </FormSection>
        <SidebarSection>
          {helpText ? (
            <HelpSection>
              <Typography variant="h3">
                {t("HELP_TEXT_FOR_TEMPLATE", { name: currentTemplate.name })}
              </Typography>
              {helpText.detail ? (
                <Typography variant="body">{helpText.detail}</Typography>
              ) : null}
              {helpText.variables?.length ? (
                <VariablesWrapper>
                  {helpText.variables.map((variable) => (
                    <ClipBoardWrapper key={variable} toCopy={variable}>
                      <VariableTag title={t("CLICK_TO_COPY")}>
                        {variable}
                      </VariableTag>
                    </ClipBoardWrapper>
                  ))}
                </VariablesWrapper>
              ) : null}
            </HelpSection>
          ) : null}
          {variables.length > 0 && (
            <VariablesHint>
              <Typography variant="h3">{t("AVAILABLE_VARIABLES")}</Typography>
              <Typography variant="body" style={{ marginBottom: "10px" }}>
                {t("CLICK_TO_COPY_VARIABLE")}
              </Typography>
              <VariablesWrapper>
                {variables.map((variable) => (
                  <ClipBoardWrapper
                    key={variable}
                    toCopy={variable}
                  >
                    <VariableTag title={t("CLICK_TO_COPY")}>
                      {variable}
                    </VariableTag>
                  </ClipBoardWrapper>
                ))}
              </VariablesWrapper>
            </VariablesHint>
          )}
        </SidebarSection>
      </Content>
    </Container>
  );
};

export default MessageTemplateEdit;
